import { useState, useEffect } from 'react';
import { Card, Table, Statistic, Row, Col, DatePicker, Button, Space, Spin, Select } from 'antd';
import { BarChartOutlined, ThunderboltOutlined, DollarOutlined, PercentageOutlined, DownloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { t } from '../utils/i18n';

const { RangePicker } = DatePicker;

const Usage = () => {
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState({ total_cost: 0, call_count: 0, total_input: 0, total_output: 0 });
  const [models, setModels] = useState([]);
  const [records, setRecords] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [dates, setDates] = useState([dayjs().startOf('month'), dayjs()]);

  const fetchAll = async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (dates[0]) params.set('start', dates[0].format('YYYY-MM-DD'));
    if (dates[1]) params.set('end', dates[1].format('YYYY-MM-DD'));
    const qs = params.toString();

    try {
      const token = localStorage.getItem('user_token');
      const headers = { 'Authorization': `Bearer ${token}` };
      const [sRes, mRes, rRes] = await Promise.all([
        fetch(`/api/usage/summary?${qs}`, { headers, credentials: 'include' }).then(r => r.json()),
        fetch(`/api/usage/by-model?${qs}`, { headers, credentials: 'include' }).then(r => r.json()),
        fetch(`/api/usage/records?${qs}&page=${page}&page_size=20`, { headers, credentials: 'include' }).then(r => r.json()),
      ]);
      if (sRes.code === 0) setSummary(sRes.data);
      if (mRes.code === 0 && mRes.data?.models) setModels(mRes.data.models);
      if (rRes.code === 0) { setRecords(rRes.data?.records || []); setTotal(rRes.data?.total || 0); }
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const exportCSV = () => {
    const params = new URLSearchParams();
    if (dates[0]) params.set('start', dates[0].format('YYYY-MM-DD'));
    if (dates[1]) params.set('end', dates[1].format('YYYY-MM-DD'));
    params.set('type', 'detail');
    const token = localStorage.getItem('user_token');
    window.open(`/api/usage/export-csv?${params.toString()}&token=${token}`, '_blank');
  };

  useEffect(() => { fetchAll(); }, []);

  const modelColumns = [
    { title: t('model'), dataIndex: 'model' },
    { title: t('total_calls'), dataIndex: 'call_count' },
    { title: t('input_tokens'), dataIndex: 'total_input' },
    { title: t('output_tokens'), dataIndex: 'total_output' },
    { title: t('amount'), dataIndex: 'amount', render: v => v ? `$${Number(v).toFixed(4)}` : '-' },
    { title: t('discount'), dataIndex: 'avg_discount', render: v => (v>=1||!v) ? <span>{t('no_discount')}</span> : <span>{parseFloat((v*10).toFixed(1))}{t('discount_unit')}</span> },
  ];

  const recordColumns = [
    { title: t('time'), dataIndex: 'created_at', render: v => v ? dayjs(v).format('YYYY-MM-DD HH:mm:ss') : '-' },
    { title: t('model'), dataIndex: 'model' },
    { title: t('input'), dataIndex: 'input_tokens', render: v => v ? Number(v).toLocaleString() : '-' },
    { title: t('output'), dataIndex: 'output_tokens', render: v => v ? Number(v).toLocaleString() : '-' },
    { title: t('amount'), dataIndex: 'amount', render: v => `$${(v||0).toFixed(6)}` },
    { title: t('discount'), dataIndex: 'discount_rate', render: v => (v>=1||!v) ? <span>{t('no_discount')}</span> : <span>{parseFloat((v*10).toFixed(1))}{t('discount_unit')}</span> },
  ];

  return (
    <div className="user-page-wrapper">
      <div className="user-page-container">
        <h2><BarChartOutlined /> {t('usage_analysis')}</h2>
        <Space style={{ marginBottom: 16 }}>
          <RangePicker value={dates} onChange={setDates} />
          <Button type="primary" onClick={fetchAll}>{t('query')}</Button>
          <Button icon={<DownloadOutlined/>} onClick={exportCSV}>{t('export_csv')}</Button>
        </Space>

        <Spin spinning={loading}>
          <Row gutter={16} style={{ marginBottom: 24 }}>
            <Col span={6}><Card><Statistic title={t('total_consumption')} value={summary.total_cost || 0} precision={4} prefix="$" /></Card></Col>
            <Col span={6}><Card><Statistic title={t('total_calls')} value={summary.call_count || 0} prefix={<ThunderboltOutlined/>} /></Card></Col>
            <Col span={6}><Card><Statistic title={t('input_tokens')} value={summary.total_input || 0} /></Card></Col>
            <Col span={6}><Card><Statistic title={t('output_tokens')} value={summary.total_output || 0} /></Card></Col>
          </Row>

          <Card title={t('by_model')} style={{ marginBottom: 24 }}>
            <Table columns={modelColumns} dataSource={models} rowKey="model" size="small" pagination={false}
              summary={() => models.length > 0 ? (
                <Table.Summary.Row>
                  <Table.Summary.Cell><strong>{t('total')}</strong></Table.Summary.Cell>
                  <Table.Summary.Cell>{models.reduce((s,r)=>s+(r.call_count||0), 0)}</Table.Summary.Cell>
                  <Table.Summary.Cell>{models.reduce((s,r)=>s+(r.total_input||0), 0)}</Table.Summary.Cell>
                  <Table.Summary.Cell>{models.reduce((s,r)=>s+(r.total_output||0), 0)}</Table.Summary.Cell>
                  <Table.Summary.Cell><strong>${models.reduce((s,r)=>s+(Number(r.amount)||0), 0).toFixed(4)}</strong></Table.Summary.Cell>
                  <Table.Summary.Cell></Table.Summary.Cell>
                </Table.Summary.Row>
              ) : null} />
          </Card>

          <Card title={t('consumption_detail')}>
            <Table columns={recordColumns} dataSource={records} rowKey={(r,i) => `${r.created_at}-${i}`} size="small"
              pagination={{ current: page, total, pageSize: 20, onChange: (p) => { setPage(p); fetchAll(); } }} />
          </Card>
        </Spin>
      </div>
    </div>
  );
};

export default Usage;
